<template>
  <div v-loading="loading" class="rep-dashboard">

    <div class="rep-dashboard-header mb-20">
      <h3 class="m-0">Mes priorités — Allocations &amp; Réservations</h3>
    </div>

    <!-- KPI Cards -->
    <el-row :gutter="16" class="mb-24">
      <el-col :xs="12" :sm="6">
        <div class="kpi-card card-base card-shadow--small">
          <i class="kpi-icon mdi mdi-tag-multiple-outline"></i>
          <div class="kpi-value">{{ kpis.totalAllocations }}</div>
          <div class="kpi-label">Allocations actives</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="kpi-card card-base card-shadow--small kpi-available">
          <i class="kpi-icon mdi mdi-check-circle-outline"></i>
          <div class="kpi-value">{{ kpis.availableAllocations }}</div>
          <div class="kpi-label">Produits disponibles</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="kpi-card card-base card-shadow--small">
          <i class="kpi-icon mdi mdi-clipboard-list-outline"></i>
          <div class="kpi-value">{{ kpis.nonCompletedReservations }}</div>
          <div class="kpi-label">Réservations non complétées</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="kpi-card card-base card-shadow--small" :class="kpis.criticalCount > 0 ? 'kpi-critical' : ''">
          <i class="kpi-icon mdi mdi-alert-circle-outline"></i>
          <div class="kpi-value">{{ kpis.criticalCount }}</div>
          <div class="kpi-label">Nécessitent attention</div>
        </div>
      </el-col>
    </el-row>

    <!-- Filter bar -->
    <div class="mb-10 flex align-center" style="gap: 12px; flex-wrap: wrap;">
      <h4 class="m-0">Liste de priorités</h4>
      <el-checkbox-group v-model="urgencyFilters" size="mini" @change="handleFilterChange">
        <el-checkbox-button label="critical">Critique</el-checkbox-button>
        <el-checkbox-button label="attention">Attention</el-checkbox-button>
        <el-checkbox-button label="ok">En cours</el-checkbox-button>
      </el-checkbox-group>
    </div>

    <!-- Priority table -->
    <el-table
      :data="paginatedPriorityItems"
      stripe
      border
      empty-text="Aucun élément à afficher"
      style="width: 100%"
    >
      <el-table-column label="Urgence" width="120">
        <template slot-scope="scope">
          <el-tag :type="scope.row.urgency.tagType" size="small">{{ scope.row.urgency.label }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Type" width="130">
        <template slot-scope="scope">
          <el-tag
            :type="scope.row.itemType === 'allocation' ? 'primary' : ''"
            size="small"
            effect="plain"
          >{{ scope.row.itemType === 'allocation' ? 'Allocation' : 'Réservation' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Référence" width="130">
        <template slot-scope="scope">
          <PortausLink :id="scope.row.id" path="/reservations/" :label="String(scope.row.id)" :openNewTab="true" />
        </template>
      </el-table-column>
      <el-table-column label="Client" min-width="220" show-overflow-tooltip>
        <template slot-scope="scope">
          <PortausLink 
            v-if="scope.row.customerId" 
            :id="scope.row.customerId" 
            path="/customers/" 
            :label="scope.row.customerName" 
            :openNewTab="true" 
          />
          <span v-else>{{ scope.row.customerName }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Produit" min-width="280">
        <template slot-scope="scope">
          <el-link type="primary" @click="openProductDrawer(scope.row.puid)">{{ scope.row.productName }}</el-link>
          <div class="fs-12 secondary-text product-meta-line">{{ scope.row.producerName }}</div>
          <div class="fs-12 secondary-text product-meta-line">UVC: {{ scope.row.uvc }} | Vol: {{ scope.row.volume }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Détail" min-width="220">
        <template slot-scope="scope">
          <template v-if="scope.row.itemType === 'allocation'">
            <span v-if="scope.row.delayed" class="fs-12 secondary-text">À venir</span>
            <template v-else>
              <el-progress
                :percentage="scope.row.progressPercentage"
                :status="scope.row.progressPercentage >= 100 ? 'success' : undefined"
              />
              <span class="fs-12 secondary-text">{{ scope.row.qtyUsed }} / {{ scope.row.qtyOriginal }}</span>
            </template>
          </template>
          <template v-else>
            <span v-if="scope.row.delayed" class="fs-12 secondary-text">À venir</span>
            <template v-else>
              <el-progress
                :percentage="scope.row.progressPercentage"
                :status="scope.row.progressPercentage >= 100 ? 'success' : undefined"
              />
              <span class="fs-12 secondary-text">
                {{ scope.row.qtyUsed }} / {{ scope.row.qtyReserved != null ? scope.row.qtyReserved : '--' }}
                <span class="secondary-text">(restant: {{ scope.row.qtyRemaining != null ? scope.row.qtyRemaining : '--' }})</span>
              </span>
            </template>
          </template>
        </template>
      </el-table-column>
      <el-table-column label="Nb jours dispo" width="140">
        <template slot-scope="scope">
          <span v-if="scope.row.daysSinceAvailability != null">
            {{ scope.row.daysSinceAvailability }}j
            <span v-if="scope.row.availableLotsCount != null" class="fs-12 secondary-text">
              ({{ scope.row.availableLotsCount }} lot{{ scope.row.availableLotsCount > 1 ? 's' : '' }})
            </span>
          </span>
          <span v-else class="secondary-text">--</span>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="showPagination" class="pagination-wrapper">
      <el-pagination
        background
        layout="prev, pager, next"
        :page-size="pageSize"
        :current-page="currentPage"
        :total="filteredPriorityItems.length"
        @current-change="handlePageChange"
      />
    </div>

    <ProductDetailDrawer
      :puid="selectedPuid"
      direction="ltr"
      :visible="productDetailDrawerVisible"
      @close="closeProductDetailDrawer"
    />
  </div>
</template>

<script>
import reservationAPI from '@/services/API/reservation';
import InventoryAPI from '@/services/API/inventory';
import PortausLink from '@/components/portaus-link';
import ProductDetailDrawer from '@/views/pages/inventory/components/product-detail-drawer';
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';

export default {
  name: 'RepReservationsDashboard',
  components: {
    PortausLink,
    ProductDetailDrawer,
  },
  data() {
    return {
      loading: false,
      allocationRows: [],
      reservationRows: [],
      urgencyFilters: ['critical', 'attention', 'ok'],
      currentPage: 1,
      pageSize: 15,
      selectedPuid: null,
      productDetailDrawerVisible: false,
    };
  },
  computed: {
    priorityItems() {
      return _.orderBy(
        [...this.allocationRows, ...this.reservationRows],
        [
          item => item.urgency.score,
          item => (item.daysSinceAvailability != null ? -item.daysSinceAvailability : 0),
        ],
        ['desc', 'desc']
      );
    },
    filteredPriorityItems() {
      return this.priorityItems.filter(item => this.urgencyFilters.includes(item.urgency.level));
    },
    paginatedPriorityItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredPriorityItems.slice(start, start + this.pageSize);
    },
    showPagination() {
      return this.filteredPriorityItems.length > this.pageSize;
    },
    kpis() {
      return {
        totalAllocations: this.allocationRows.length,
        availableAllocations: this.allocationRows.filter(r => !r.delayed).length,
        nonCompletedReservations: this.reservationRows.length,
        criticalCount: this.priorityItems.filter(i => i.urgency.level === 'critical').length,
      };
    },
    ...mapGetters(['getCurrentUserId']),
  },
  methods: {
    formatDate(date) {
      if (!date) return '--';
      return dayjs(date).format('YYYY-MM-DD');
    },
    getCustomerName(customer) {
      const usualName = _.trim(_.get(customer, 'usual_name', ''));
      if (usualName !== '') return usualName;
      const name = _.trim(_.get(customer, 'name', ''));
      return name !== '' ? name : '--';
    },
    getUrgencyForAllocation(row) {
      if (row.delayed) return { level: 'coming', label: 'À venir', score: 0, tagType: 'info' };
      const daysSinceAvailability = Number(row.daysSinceAvailability);
      const hasVeryLongDelay = Number.isFinite(daysSinceAvailability) && daysSinceAvailability > 100;
      if (row.progressPercentage === 0 && hasVeryLongDelay) return { level: 'critical', label: 'Critique', score: 3, tagType: 'danger' };
      if (row.progressPercentage < 50 && hasVeryLongDelay) return { level: 'attention', label: 'Attention', score: 2, tagType: 'warning' };
      return { level: 'ok', label: 'En cours', score: 1, tagType: 'success' };
    },
    getUrgencyForReservation(row) {
      if (row.delayed) return { level: 'coming', label: 'À venir', score: 0, tagType: 'info' };
      const ratio = row.qtyReserved > 0 ? (row.qtyRemaining / row.qtyReserved) : 0;
      const daysSinceAvailability = Number(row.daysSinceAvailability);
      const hasCriticalDelay = Number.isFinite(daysSinceAvailability) && daysSinceAvailability > 100;
      const hasAttentionDelay = Number.isFinite(daysSinceAvailability) && daysSinceAvailability > 100;
      if (ratio >= 1 && hasCriticalDelay) return { level: 'critical', label: 'Critique', score: 3, tagType: 'danger' };
      if (ratio > 0.5 && hasAttentionDelay) return { level: 'attention', label: 'Attention', score: 2, tagType: 'warning' };
      return { level: 'ok', label: 'En cours', score: 1, tagType: 'success' };
    },
    buildAllocationRow(allocation, delayInfoByPuid = {}) {
      const qtyOriginal = Number(_.get(allocation, 'qty', 0)) || 0;
      const availableQty = Number(_.get(allocation, 'availableQty', _.get(allocation, 'quantity.available', 0))) || 0;
      const qtyUsed = Math.max(0, qtyOriginal - availableQty);
      const progressPercentage = qtyOriginal > 0
        ? Math.min(100, Math.max(0, Math.round((qtyUsed / qtyOriginal) * 100)))
        : 0;
      const puid = _.get(allocation, 'puid', _.get(allocation, 'product.puid', null));
      const delayed = _.get(allocation, 'delayed', false) === true;
      const daysSinceAvailability = delayed ? null : _.get(delayInfoByPuid, [puid, 'nbDays'], null);
      const availableLotsCount = delayed ? null : _.get(delayInfoByPuid, [puid, 'availableLotsCount'], null);

      const row = {
        itemType: 'allocation',
        id: allocation.id,
        puid,
        delayed,
        daysSinceAvailability,
        availableLotsCount,
        customerName: this.getCustomerName(_.get(allocation, 'customer', {})),
        customerId: _.get(allocation, 'customer.id', null),
        productName: _.get(allocation, 'product.name', '--'),
        producerName: _.get(allocation, 'product.provider.name', '--'),
        uvc: _.get(allocation, 'product.uvc', '--'),
        volume: _.get(allocation, 'product.volume', '--'),
        qtyOriginal,
        qtyUsed,
        progressPercentage,
      };
      row.urgency = this.getUrgencyForAllocation(row);
      return row;
    },
    buildReservationRow(reservation, delayInfoByPuid = {}) {
      const puid = _.get(reservation, 'puid', _.get(reservation, 'product.puid', null));
      const qtyReserved = Number(_.get(reservation, 'quantity.reserved', _.get(reservation, 'qty', 0))) || 0;
      const qtyRemaining = Number(_.get(reservation, 'quantity.available', _.get(reservation, 'availableQty', 0))) || 0;
      const qtyUsed = Math.max(0, qtyReserved - qtyRemaining);
      const progressPercentage = qtyReserved > 0
        ? Math.min(100, Math.max(0, Math.round((qtyUsed / qtyReserved) * 100)))
        : 0;
      const nbDays = _.get(delayInfoByPuid, [puid, 'nbDays'], null);

      const row = {
        itemType: 'reservation',
        id: reservation.id,
        puid,
        delayed: nbDays == null,
        daysSinceAvailability: nbDays,
        availableLotsCount: _.get(delayInfoByPuid, [puid, 'availableLotsCount'], null),
        customerName: this.getCustomerName(_.get(reservation, 'customer', {})),
        customerId: _.get(reservation, 'customer.id', null),
        productName: _.get(reservation, 'product.name', '--'),
        producerName: _.get(reservation, 'product.provider.name', '--'),
        uvc: _.get(reservation, 'product.uvc', '--'),
        volume: _.get(reservation, 'product.volume', '--'),
        qtyReserved,
        qtyRemaining,
        qtyUsed,
        progressPercentage,
      };
      row.urgency = this.getUrgencyForReservation(row);
      return row;
    },
    getAvailableLotsCountFromProduct(product) {
      const items = _.get(product, 'items', []);
      if (!Array.isArray(items) || items.length === 0) return null;
      return items.filter((item) => {
        const onHand = Number(_.get(item, 'quantity.onHand', _.get(item, 'quantity.on_hand', _.get(item, 'qty', 0))));
        return Number.isFinite(onHand) && onHand > 0;
      }).length;
    },
    async fetchDelayInfoByPuid(puids) {
      if (!Array.isArray(puids) || puids.length === 0) return {};
      const products = await InventoryAPI.fetchProductsV2({ puid: puids, paging: 'no' });
      const map = {};
      const productsArray = Array.isArray(products) ? products : _.get(products, 'data', []);
      for (const product of productsArray) {
        const puid = _.get(product, 'puid');
        if (!puid) continue;
        const nbDays = _.get(product, 'quantity.nbDays', null);
        let availableLotsCount = this.getAvailableLotsCountFromProduct(product);
        if (availableLotsCount === null && nbDays != null) {
          try {
            const fullProduct = await InventoryAPI.getInventoryProductV2(puid);
            availableLotsCount = this.getAvailableLotsCountFromProduct(fullProduct);
          } catch (e) {
            availableLotsCount = null;
          }
        }
        map[puid] = { nbDays, availableLotsCount };
      }
      return map;
    },
    async fetchData() {
      if (!this.getCurrentUserId) return;
      this.loading = true;
      try {
        const repId = this.getCurrentUserId;
        const [allocationsResponse, allReservationsResponse] = await Promise.all([
          reservationAPI.fetchRepresentativeAllocationsReport({
            alloc: true,
            repId,
            paging: 'false',
            orderBy: 'createdAt',
            direction: 'DESC',
          }),
          reservationAPI.fetchReservationsV2({
            repId,
            paging: 'false',
            orderBy: 'createdAt',
            direction: 'DESC',
          }),
        ]);

        const allocations = Array.isArray(allocationsResponse) ? allocationsResponse : [];
        const allReservations = Array.isArray(allReservationsResponse) ? allReservationsResponse : [];
        const nonCompletedReservations = allReservations.filter(r => _.get(r, 'completed') !== true);

        const puids = _.chain(allocations)
          .map(a => _.get(a, 'puid', _.get(a, 'product.puid', null)))
          .concat(nonCompletedReservations.map(r => _.get(r, 'puid', _.get(r, 'product.puid', null))))
          .filter(Boolean)
          .uniq()
          .value();

        const delayInfoByPuid = await this.fetchDelayInfoByPuid(puids);

        this.allocationRows = allocations.map(a => this.buildAllocationRow(a, delayInfoByPuid));
        this.reservationRows = nonCompletedReservations
          .map(r => this.buildReservationRow(r, delayInfoByPuid))
          .filter(r => r.delayed !== true);
      } catch (e) {
        this.$message.error('Erreur lors du chargement des données.');
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() {
      this.currentPage = 1;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    openProductDrawer(puid) {
      if (!puid) return;
      this.selectedPuid = puid;
      this.productDetailDrawerVisible = true;
    },
    closeProductDetailDrawer() {
      this.selectedPuid = null;
      this.productDetailDrawerVisible = false;
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.rep-dashboard {
  margin-top: 20px;
}

.rep-dashboard-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.kpi-card {
  padding: 20px;
  text-align: center;
  margin-bottom: 16px;
  border-radius: 6px;

  .kpi-icon {
    font-size: 1.6rem;
    color: #909399;
    display: block;
    margin-bottom: 6px;
  }

  .kpi-value {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.1;
  }

  .kpi-label {
    font-size: 0.8rem;
    color: #909399;
    margin-top: 4px;
  }

  &.kpi-available .kpi-value {
    color: #67c23a;
  }

  &.kpi-critical .kpi-value {
    color: #f56c6c;
  }
}

.product-meta-line {
  line-height: 1.1;
  margin: 0;
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}
</style>
